import { LockKeyhole } from "lucide-react";
import React, { FC, ReactElement, ReactNode, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton, IgTooltip } from "@intelligems/ig-theme";
import { IntelligemsAppScopeType } from "@intelligems/ig-types";
import { ButtonProps } from "@mui/material";
import { useAdminHasFullAccess } from "@src/api/user-auths/use-admin-has-full-access";

import { useIsAdmin } from "../../api/user-auths/is-admin";
import { setDialog } from "../../store/slices/global";

import { useHasRequiredShopifyScopes } from "./hooks";

interface LimitedAccessButtonProps extends ButtonProps {
  isEnabled: boolean;
  children: ReactNode;
  debugId: string;
  customDisabledTooltipMessage?: ReactNode;
  requiredShopifyScopes?: IntelligemsAppScopeType[];
  loading?: boolean;
  modalPopupProps?: {
    title: string;
    message: string;
    yesButtonText: string;
    noButtonText: string;
    yesButtonAction: any;
    noButtonAction: any;
  };
  adminDisable?: boolean; // when a component should be disabled for admins too
  override?: "enabled" | "disabled";
  customTooltip?: ReactElement<any>;
  customColor?: string;
  customBackgroundColor?: string;
}

export const ScopedButton: FC<LimitedAccessButtonProps> = ({
  isEnabled,
  loading,
  children,
  adminDisable,
  override,
  customTooltip,
  debugId,
  customColor,
  customBackgroundColor,
  customDisabledTooltipMessage,
  requiredShopifyScopes,
  modalPopupProps,
  ...props
}) => {
  const dispatch = useDispatch();
  const isAdmin = useIsAdmin();
  const hasRequiredShopifyScopes = useHasRequiredShopifyScopes(
    requiredShopifyScopes || []
  );
  const adminHasFullAccess = useAdminHasFullAccess();

  const doAdminsHaveAccess = isAdmin && adminHasFullAccess;

  const [previousEnabled, setPreviousEnabled] = useImmer<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    if (previousEnabled === undefined) {
      setPreviousEnabled(isEnabled);
    }
  }, [isEnabled]);

  useEffect(() => {
    if (
      previousEnabled == true &&
      !doAdminsHaveAccess &&
      !isEnabled &&
      modalPopupProps
    ) {
      setPreviousEnabled(isEnabled);
      dispatch(
        setDialog({
          dialog: "scoped",
          open: true,
          props: {
            title: modalPopupProps.title,
            message: modalPopupProps.message,
            yesButtonText: modalPopupProps.yesButtonText,
            yesButtonAction: modalPopupProps.yesButtonAction,
            noButtonText: modalPopupProps.noButtonText,
            noButtonAction: modalPopupProps.noButtonAction,
          },
        })
      );
    }
  }, [isEnabled]);

  if (!hasRequiredShopifyScopes) {
    return (
      <IgButton
        {...props}
        onClick={() => {
          dispatch(setDialog({ dialog: "missingShopifyScope", open: true }));
        }}
      >
        {children}
      </IgButton>
    );
  }

  if (doAdminsHaveAccess && !adminDisable) {
    return <IgButton {...props}>{children}</IgButton>;
  }

  return (
    <IgTooltip
      title={
        isEnabled
          ? ""
          : customDisabledTooltipMessage ||
            "Please contact Intelligems to unlock this feature"
      }
    >
      <span>
        <IgButton
          startIcon={!isEnabled && <LockKeyhole size={18} />}
          disabled={!isEnabled}
          {...props}
        >
          {children}
        </IgButton>
      </span>
    </IgTooltip>
  );
};
